import type { ElementRegistry } from "../core/element-registry"
import { ContainerRenderer } from "./container-renderer"
import { TextRenderer } from "./text-renderer"
import { ImageRenderer } from "./image-renderer"
import { ButtonRenderer } from "./button-renderer"
import { BadgeRenderer } from "./badge-renderer"
import { IconRenderer } from "./icon-renderer"
import { CardRenderer } from "./card-renderer"
import { InputRenderer } from "./input-renderer"
import { SeparatorRenderer } from "./separator-renderer"
import { SkeletonRenderer } from "./skeleton-renderer"
import { PlaceholderRenderer } from "./placeholder-renderer"
import { TabsRenderer } from "./tabs-renderer"
import { TabTriggerRenderer } from "./tab-trigger-renderer"
import { BlockRefRenderer } from "./block-ref-renderer"
// Phase 1: Form renderers
import { LabelRenderer } from "./label-renderer"
import { TextareaRenderer } from "./textarea-renderer"
import { CheckboxRenderer } from "./checkbox-renderer"
import { RadioRenderer } from "./radio-renderer"
import { SwitchRenderer } from "./switch-renderer"
import { SliderRenderer } from "./slider-renderer"
import { SelectRenderer } from "./select-renderer"
import { FormRenderer } from "./form-renderer"
// Phase 2: Overlay & Feedback renderers
import { DialogRenderer } from "./dialog-renderer"
import { AlertDialogRenderer } from "./alert-dialog-renderer"
import { SheetRenderer } from "./sheet-renderer"
import { DrawerRenderer } from "./drawer-renderer"
import { PopoverRenderer } from "./popover-renderer"
import { DropdownMenuRenderer } from "./dropdown-menu-renderer"
import { AlertRenderer } from "./alert-renderer"
import { ProgressRenderer } from "./progress-renderer"
import { ToastRenderer } from "./toast-renderer"
import { NavigationMenuRenderer } from "./navigation-menu-renderer"
// Phase 3: Data Display renderers
import { AccordionRenderer } from "./accordion-renderer"
import { AccordionItemRenderer } from "./accordion-item-renderer"
import { TableRenderer } from "./table-renderer"
import { DataTableRenderer } from "./data-table-renderer"
import { ChartRenderer } from "./chart-renderer"
import { CarouselRenderer } from "./carousel-renderer"
// Phase 4: Missing renderers (discovered via zero-base verification)
import { ListRenderer } from "./list-renderer"
import { AvatarRenderer, AvatarGroupRenderer } from "./avatar-renderer"
import { LinkRenderer } from "./link-renderer"
// Phase 6: Interactive renderers
import { TooltipRenderer } from "./tooltip-renderer"
// Phase 7: Navigation & Form renderers
import { BreadcrumbRenderer } from "./breadcrumb-renderer"
import { PaginationRenderer } from "./pagination-renderer"
import { DatePickerRenderer } from "./date-picker-renderer"
import { CalendarRenderer } from "./calendar-renderer"
import { ComboboxRenderer } from "./combobox-renderer"
// Phase 8: Feedback & Collapsible renderers
import { CollapsibleRenderer } from "./collapsible-renderer"
// Phase 9: Advanced interactive renderers
import { CommandRenderer } from "./command-renderer"
import { HoverCardRenderer } from "./hover-card-renderer"
import { InputOtpRenderer } from "./input-otp-renderer"
import { MenubarRenderer } from "./menubar-renderer"
import { ResizableRenderer } from "./resizable-renderer"
import { ToggleRenderer } from "./toggle-renderer"
import { ToggleGroupRenderer } from "./toggle-group-renderer"
// Phase 10: Flutter-exclusive interactive renderers
import { MonthPickerRenderer } from "./month-picker-renderer"
import { StarRatingRenderer } from "./star-rating-renderer"
import { ColorPickerRenderer } from "./color-picker-renderer"
import { ChipInputRenderer } from "./chip-input-renderer"
import { PhoneInputRenderer } from "./phone-input-renderer"
import { StepperRenderer } from "./stepper-renderer"
import { TimelineRenderer } from "./timeline-renderer"
import { TreeRenderer } from "./tree-renderer"
// Phase 11: Sidebar renderers
import { SidebarRenderer, SidebarInsetRenderer, SidebarTriggerRenderer } from "./sidebar-renderer"
// Phase 12: Semantic renderers
import { FieldRenderer } from "./field-renderer"
import { FieldGroupRenderer } from "./field-group-renderer"
import { InputGroupRenderer } from "./input-group-renderer"
import { EmptyRenderer } from "./empty-renderer"
import { ItemRenderer } from "./item-renderer"
import { KbdRenderer } from "./kbd-renderer"
import { ButtonGroupRenderer } from "./button-group-renderer"
import { NativeSelectRenderer } from "./native-select-renderer"
import { RingProgressRenderer } from "./ring-progress-renderer"
import { WaveformRenderer } from "./waveform-renderer"
// Phase 13: Theme-v2 M2 deferred atoms (loader / aspect-ratio / scroll-area)
import { LoaderRenderer } from "./loader-renderer"
import { AspectRatioRenderer } from "./aspect-ratio-renderer"
import { ScrollAreaRenderer } from "./scroll-area-renderer"
// Phase 14: Lottie (post-M2) — final deferred atom, lazy-loaded runtime
import { LottieRenderer } from "./lottie-renderer"
// Slot renderer factory
import { createSlotRenderer, createSlotTextRenderer } from "./slot-renderer"
// Feature-island renderer (Phase 3 shell-as-cogo-app Prereq D)
import { IslandRenderer } from "./island-renderer"
// shadcn compound components for slot registration
import { CardHeader, CardTitle, CardDescription, CardAction, CardContent, CardFooter } from "../components/ui/card"
import { ItemHeader, ItemFooter, ItemContent, ItemTitle, ItemDescription, ItemActions, ItemMedia, ItemSeparator } from "../components/ui/item"
import { EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent } from "../components/ui/empty"
import { AlertTitle, AlertDescription } from "../components/ui/alert"
import { FieldLabel, FieldContent, FieldDescription } from "../components/ui/field"
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group"
import { ButtonGroup, ButtonGroupSeparator, ButtonGroupText } from "../components/ui/button-group"
import { InputGroup, InputGroupAddon } from "../components/ui/input-group"
import { TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableFooter } from "../components/ui/table"
import { ToggleGroupItem } from "../components/ui/toggle-group"
import { DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuGroup, DropdownMenuLabel } from "../components/ui/dropdown-menu"
import { SelectTrigger, SelectContent } from "../components/ui/select"
import { AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "../components/ui/alert-dialog"

export function registerAllRenderers(registry: ElementRegistry): void {
  // Existing renderers
  registry.register("container", ContainerRenderer)
  registry.register("section", ContainerRenderer)
  // HTML5 semantic element aliases — render as container with appropriate htmlTag
  registry.register("header", ContainerRenderer)
  registry.register("footer", ContainerRenderer)
  registry.register("nav", ContainerRenderer)
  registry.register("main", ContainerRenderer)
  registry.register("aside", ContainerRenderer)
  registry.register("article", ContainerRenderer)
  registry.register("text", TextRenderer)
  // "heading" is a semantic alias for text with level attribute (h1-h6).
  // TextRenderer reads element.text + className and renders correctly.
  // Registered here (not gap-listed) because RN also registers "heading"
  // and the type appears in 10+ blocks (card, form, footer categories).
  registry.register("heading", TextRenderer)
  registry.register("image", ImageRenderer)
  registry.register("button", ButtonRenderer)
  registry.register("badge", BadgeRenderer)
  registry.register("icon", IconRenderer)
  registry.register("card", CardRenderer)
  registry.register("input", InputRenderer)
  registry.register("separator", SeparatorRenderer)
  registry.register("divider", SeparatorRenderer)
  registry.register("skeleton", SkeletonRenderer)
  registry.register("placeholder", PlaceholderRenderer)
  registry.register("tabs", TabsRenderer)
  registry.register("tab-trigger", TabTriggerRenderer)
  registry.register("block-ref", BlockRefRenderer)
  // Feature-island renderer (Phase 3 shell-as-cogo-app Prereq D)
  registry.register("island", IslandRenderer)
  // Phase 1: Form renderers
  registry.register("label", LabelRenderer)
  registry.register("textarea", TextareaRenderer)
  registry.register("checkbox", CheckboxRenderer)
  registry.register("radio", RadioRenderer)
  registry.register("switch", SwitchRenderer)
  registry.register("slider", SliderRenderer)
  registry.register("select", SelectRenderer)
  registry.register("form", FormRenderer)
  // Phase 2: Overlay & Feedback renderers
  registry.register("dialog", DialogRenderer)
  registry.register("alert-dialog", AlertDialogRenderer)
  registry.register("sheet", SheetRenderer)
  registry.register("drawer", DrawerRenderer)
  registry.register("popover", PopoverRenderer)
  registry.register("dropdown-menu", DropdownMenuRenderer)
  registry.register("alert", AlertRenderer)
  registry.register("progress", ProgressRenderer)
  registry.register("radial-progress", ProgressRenderer)
  registry.register("toast", ToastRenderer)
  registry.register("navigation-menu", NavigationMenuRenderer)
  // Phase 3: Data Display renderers
  registry.register("accordion", AccordionRenderer)
  registry.register("accordion-item", AccordionItemRenderer)
  registry.register("table", TableRenderer)
  registry.register("data-table", DataTableRenderer)
  registry.register("data_table", DataTableRenderer)  // snake_case alias (some blocks use this)
  registry.register("chart", ChartRenderer)
  registry.register("carousel", CarouselRenderer)
  // Phase 4: Missing renderers
  registry.register("list", ListRenderer)
  registry.register("avatar", AvatarRenderer)
  registry.register("avatar-group", AvatarGroupRenderer)
  // Phase 5: Navigation
  registry.register("link", LinkRenderer)
  // Phase 6: Interactive renderers
  registry.register("tooltip", TooltipRenderer)
  // Phase 7: Navigation & Form renderers
  registry.register("breadcrumb", BreadcrumbRenderer)
  registry.register("pagination", PaginationRenderer)
  registry.register("date-picker", DatePickerRenderer)
  registry.register("calendar", CalendarRenderer)
  registry.register("combobox", ComboboxRenderer)
  // Phase 8: Feedback & Collapsible renderers
  registry.register("collapsible", CollapsibleRenderer)
  // Phase 9: Advanced interactive renderers
  registry.register("command", CommandRenderer)
  registry.register("hover-card", HoverCardRenderer)
  registry.register("input-otp", InputOtpRenderer)
  registry.register("menubar", MenubarRenderer)
  registry.register("resizable", ResizableRenderer)
  registry.register("toggle", ToggleRenderer)
  registry.register("toggle-group", ToggleGroupRenderer)
  // Phase 10: Flutter-exclusive interactive renderers
  registry.register("star-rating", StarRatingRenderer)
  registry.register("color-picker", ColorPickerRenderer)
  registry.register("chip-input", ChipInputRenderer)
  registry.register("phone-input", PhoneInputRenderer)
  registry.register("stepper", StepperRenderer)
  registry.register("timeline", TimelineRenderer)
  registry.register("tree", TreeRenderer)
  // Phase 10: Month picker
  registry.register("month-picker", MonthPickerRenderer)
  // Phase 11: Sidebar
  registry.register("sidebar", SidebarRenderer)
  registry.register("sidebar-inset", SidebarInsetRenderer)
  registry.register("sidebar-trigger", SidebarTriggerRenderer)
  // Phase 12: Semantic renderers
  registry.register("field", FieldRenderer)
  registry.register("field-group", FieldGroupRenderer)
  registry.register("input-group", InputGroupRenderer)
  registry.register("empty", EmptyRenderer)
  registry.register("item", ItemRenderer)
  registry.register("item-group", ItemRenderer)
  registry.register("kbd", KbdRenderer)
  registry.register("button-group", ButtonGroupRenderer)
  registry.register("native-select", NativeSelectRenderer)
  registry.register("ring-progress", RingProgressRenderer)
  registry.register("waveform", WaveformRenderer)
  // Phase 13: Theme-v2 M2 deferred atoms (consume default-theme.json defaults)
  registry.register("loader", LoaderRenderer)
  registry.register("aspect-ratio", AspectRatioRenderer)
  registry.register("scroll-area", ScrollAreaRenderer)
  // Phase 14: Lottie (post-M2) — closes canonical atom coverage 46→47/47
  registry.register("lottie", LottieRenderer)

  // ── Slot renderers: type → shadcn React component 1:1 ──
  // No internal transformation. className pass-through. Children recursive.
  const slot = createSlotRenderer
  const slotText = createSlotTextRenderer

  // Card
  registry.register("card-header", slot(CardHeader))
  registry.register("card-title", slotText(CardTitle))
  registry.register("card-description", slotText(CardDescription))
  registry.register("card-action", slot(CardAction))
  registry.register("card-content", slot(CardContent))
  registry.register("card-footer", slot(CardFooter))
  // Item
  registry.register("item-header", slot(ItemHeader))
  registry.register("item-content", slot(ItemContent))
  registry.register("item-title", slotText(ItemTitle))
  registry.register("item-description", slotText(ItemDescription))
  registry.register("item-actions", slot(ItemActions))
  registry.register("item-media", slot(ItemMedia))
  registry.register("item-separator", slot(ItemSeparator))
  registry.register("item-footer", slot(ItemFooter))
  // Empty
  registry.register("empty-header", slot(EmptyHeader))
  registry.register("empty-icon", slot(EmptyMedia, { variant: "icon" }))
  registry.register("empty-title", slotText(EmptyTitle))
  registry.register("empty-description", slotText(EmptyDescription))
  registry.register("empty-content", slot(EmptyContent))
  // Alert
  registry.register("alert-title", slotText(AlertTitle))
  registry.register("alert-description", slotText(AlertDescription))
  // Field
  registry.register("field-label", slotText(FieldLabel))
  registry.register("field-description", slotText(FieldDescription))
  registry.register("field-content", slot(FieldContent))
  registry.register("field-set", ContainerRenderer)
  registry.register("field-legend", slotText(FieldLabel))
  // Tabs (shadcn components, not ContainerRenderer)
  registry.register("tabs-list", slot(TabsList))
  registry.register("tabs-trigger", slotText(TabsTrigger))
  registry.register("tabs-content", slot(TabsContent))
  // Table
  registry.register("table-header", slot(TableHeader))
  registry.register("table-body", slot(TableBody))
  registry.register("table-row", slot(TableRow))
  registry.register("table-head", slotText(TableHead))
  registry.register("table-cell", slotText(TableCell))
  registry.register("table-footer", slot(TableFooter))
  // Select
  registry.register("select-trigger", slot(SelectTrigger))
  registry.register("select-content", slot(SelectContent))
  // Toggle group
  registry.register("toggle-group-item", slotText(ToggleGroupItem))
  // Radio group
  registry.register("radio-group", slot(RadioGroup))
  registry.register("radio-group-item", slot(RadioGroupItem))
  // Native select
  registry.register("native-select-wrapper", ContainerRenderer)
  // Dropdown menu
  registry.register("dropdown-menu-trigger", slot(DropdownMenuTrigger))
  registry.register("dropdown-menu-content", slot(DropdownMenuContent))
  registry.register("dropdown-menu-item", slotText(DropdownMenuItem))
  registry.register("dropdown-menu-separator", slot(DropdownMenuSeparator))
  registry.register("dropdown-menu-group", slot(DropdownMenuGroup))
  registry.register("dropdown-menu-label", slotText(DropdownMenuLabel))
  // Alert dialog slots (compound components)
  registry.register("alert-dialog-trigger", slotText(AlertDialogTrigger))
  registry.register("alert-dialog-content", slot(AlertDialogContent))
  registry.register("alert-dialog-header", slot(AlertDialogHeader))
  registry.register("alert-dialog-footer", slot(AlertDialogFooter))
  registry.register("alert-dialog-title", slotText(AlertDialogTitle))
  registry.register("alert-dialog-description", slotText(AlertDialogDescription))
  registry.register("alert-dialog-action", slotText(AlertDialogAction))
  registry.register("alert-dialog-cancel", slotText(AlertDialogCancel))
  // Button group
  registry.register("button-group", slot(ButtonGroup))
  registry.register("button-group-separator", slot(ButtonGroupSeparator))
  registry.register("button-group-text", slotText(ButtonGroupText))
  // Progress
  registry.register("progress-indicator", ContainerRenderer)
}

export { ElementRenderer } from "./element-renderer"
export { ContainerRenderer } from "./container-renderer"
export { TextRenderer } from "./text-renderer"
export { ImageRenderer } from "./image-renderer"
export { ButtonRenderer } from "./button-renderer"
export { BadgeRenderer } from "./badge-renderer"
export { IconRenderer } from "./icon-renderer"
export { CardRenderer } from "./card-renderer"
export { InputRenderer } from "./input-renderer"
export { SeparatorRenderer } from "./separator-renderer"
export { SkeletonRenderer } from "./skeleton-renderer"
export { PlaceholderRenderer } from "./placeholder-renderer"
export { LabelRenderer } from "./label-renderer"
export { TextareaRenderer } from "./textarea-renderer"
export { CheckboxRenderer } from "./checkbox-renderer"
export { RadioRenderer } from "./radio-renderer"
export { SwitchRenderer } from "./switch-renderer"
export { SliderRenderer } from "./slider-renderer"
export { SelectRenderer } from "./select-renderer"
export { FormRenderer } from "./form-renderer"
export { DialogRenderer } from "./dialog-renderer"
export { AlertDialogRenderer } from "./alert-dialog-renderer"
export { SheetRenderer } from "./sheet-renderer"
export { DrawerRenderer } from "./drawer-renderer"
export { PopoverRenderer } from "./popover-renderer"
export { DropdownMenuRenderer } from "./dropdown-menu-renderer"
export { AlertRenderer } from "./alert-renderer"
export { ProgressRenderer } from "./progress-renderer"
export { ToastRenderer } from "./toast-renderer"
export { NavigationMenuRenderer } from "./navigation-menu-renderer"
export { AccordionRenderer } from "./accordion-renderer"
export { AccordionItemRenderer } from "./accordion-item-renderer"
export { TableRenderer } from "./table-renderer"
export { DataTableRenderer } from "./data-table-renderer"
export { ChartRenderer } from "./chart-renderer"
export { CarouselRenderer } from "./carousel-renderer"
export { ListRenderer } from "./list-renderer"
export { AvatarRenderer } from "./avatar-renderer"
export { LinkRenderer } from "./link-renderer"
export { TooltipRenderer } from "./tooltip-renderer"
export { BreadcrumbRenderer } from "./breadcrumb-renderer"
export { PaginationRenderer } from "./pagination-renderer"
export { DatePickerRenderer } from "./date-picker-renderer"
export { CalendarRenderer } from "./calendar-renderer"
export { ComboboxRenderer } from "./combobox-renderer"
export { CollapsibleRenderer } from "./collapsible-renderer"
export { CommandRenderer } from "./command-renderer"
export { HoverCardRenderer } from "./hover-card-renderer"
export { InputOtpRenderer } from "./input-otp-renderer"
export { MenubarRenderer } from "./menubar-renderer"
export { ResizableRenderer } from "./resizable-renderer"
export { ToggleRenderer } from "./toggle-renderer"
export { ToggleGroupRenderer } from "./toggle-group-renderer"
export { StarRatingRenderer } from "./star-rating-renderer"
export { ColorPickerRenderer } from "./color-picker-renderer"
export { ChipInputRenderer } from "./chip-input-renderer"
export { PhoneInputRenderer } from "./phone-input-renderer"
export { StepperRenderer } from "./stepper-renderer"
export { TimelineRenderer } from "./timeline-renderer"
export { TreeRenderer } from "./tree-renderer"
export { MonthPickerRenderer } from "./month-picker-renderer"
export { SidebarRenderer, SidebarInsetRenderer, SidebarTriggerRenderer } from "./sidebar-renderer"
// Phase 12: Semantic renderers
export { FieldRenderer } from "./field-renderer"
export { FieldGroupRenderer } from "./field-group-renderer"
export { InputGroupRenderer } from "./input-group-renderer"
export { EmptyRenderer } from "./empty-renderer"
export { ItemRenderer } from "./item-renderer"
export { KbdRenderer } from "./kbd-renderer"
export { ButtonGroupRenderer } from "./button-group-renderer"
export { NativeSelectRenderer } from "./native-select-renderer"
// Phase 13: Theme-v2 M2 deferred atoms
export { LoaderRenderer } from "./loader-renderer"
export { AspectRatioRenderer } from "./aspect-ratio-renderer"
export { ScrollAreaRenderer } from "./scroll-area-renderer"
// Phase 14: Lottie (post-M2) — final canonical atom renderer (47/47 coverage)
export { LottieRenderer } from "./lottie-renderer"
